<template>
  <div class="areas-list">
    <v-row align="center" class="pa-4 mb-2 areas-toolbar">
      <v-card-title class="areas-toolbar__title">Районы обслуживания</v-card-title>

      <div class="areas-toolbar__search">
        <BaseTextField
          v-model="filterName"
          type-input="string"
          :is-button="false"
          label="Название"
        />
      </div>

      <v-spacer />

      <div class="areas-toolbar__actions">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              color="#4C6C93"
              class="border card-footer"
              v-bind="attrs"
              v-on="on"
              :loading="loading"
              @click="loadData"
            >
              <v-icon style="font-size: 40px; height: inherit;" color="white">mdi-reload</v-icon>
            </v-btn>
          </template>
          <span>Обновить</span>
        </v-tooltip>

        <v-tooltip v-if="$checkPermission('store_service_area')" bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              color="green"
              class="border card-footer card-footer--create"
              v-bind="attrs"
              v-on="on"
              @click="openCreate"
            >
              <v-icon style="font-size: 40px; height: inherit;" color="white">mdi-new-box</v-icon>
            </v-btn>
          </template>
          <span>Новый район обслуживания</span>
        </v-tooltip>
      </div>
    </v-row>

    <v-data-table
      class="table"
      :headers="headers"
      :items="filteredItems"
      :loading="loading"
      :expanded.sync="expanded"
      :page.sync="page"
      :items-per-page.sync="itemsPerPage"
      :footer-props="footerProps"
      single-expand
      item-key="id"
    >
      <template v-slot:item.master="{ item }">
        <span v-if="masterName(item)" class="areas-masters-text">{{ masterName(item) }}</span>
        <span v-else class="grey--text">—</span>
      </template>

      <template v-slot:item.equipment_counts="{ item }">
        <div v-if="hasEquipment(item.equipment_counts)" class="service-area-sectors__equipment">
          <v-chip
            v-for="chip in equipmentChips(item.equipment_counts)"
            :key="chip.key"
            x-small
            class="service-area-sectors__equipment-chip"
            outlined
          >
            {{ chip.label }}: {{ chip.value }}
          </v-chip>
        </div>
        <span v-else class="grey--text">—</span>
      </template>

      <template v-slot:item.actions="{ item }">
        <div class="areas-table-actions" @click.stop>
          <v-tooltip v-if="$checkPermission('update_service_area')" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                text
                tile
                small
                v-bind="attrs"
                v-on="on"
                class="btn__icon pa-0 mr-1"
                width="18"
                height="18"
                @click="openEdit(item)"
              >
                <v-icon size="large">mdi-pencil-outline</v-icon>
              </v-btn>
            </template>
            <span>Редактировать</span>
          </v-tooltip>

          <v-tooltip v-if="$checkPermission('destroy_service_area')" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                text
                tile
                small
                v-bind="attrs"
                v-on="on"
                class="btn__icon pa-0 mr-1"
                width="18"
                height="18"
                @click="confirmDelete(item)"
              >
                <v-icon size="large">mdi-close</v-icon>
              </v-btn>
            </template>
            <span>Удалить</span>
          </v-tooltip>

          <v-tooltip v-if="item.mto_sectors_count" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                text
                tile
                small
                v-bind="attrs"
                v-on="on"
                class="btn__icon pa-0"
                width="18"
                height="18"
                @click="toggleExpand(item)"
              >
                <v-icon size="large">
                  {{ isExpanded(item) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                </v-icon>
              </v-btn>
            </template>
            <span>{{ isExpanded(item) ? 'Свернуть участки' : 'Показать участки' }}</span>
          </v-tooltip>
        </div>
      </template>

      <template v-slot:expanded-item="{ headers, item }">
        <td :colspan="headers.length" class="service-area-expand-cell">
          <div class="service-area-sectors">
            <div class="service-area-sectors__header">
              <v-icon small color="primary" class="mr-2">mdi-map-marker-radius</v-icon>
              <span class="service-area-sectors__title">Участки МТО</span>
              <v-chip x-small class="ml-2" color="primary" outlined>
                {{ item.mto_sectors_count || 0 }}
              </v-chip>
            </div>

            <div v-if="item.mto_sectors && item.mto_sectors.length" class="service-area-sectors__table">
              <div class="service-area-sectors__row service-area-sectors__row--head">
                <div class="service-area-sectors__col service-area-sectors__col--id">ID</div>
                <div class="service-area-sectors__col service-area-sectors__col--name">Название</div>
                <div class="service-area-sectors__col service-area-sectors__col--equipment">Показатели</div>
                <div class="service-area-sectors__col service-area-sectors__col--actions" />
              </div>

              <div
                v-for="sector in item.mto_sectors"
                :key="sector.id"
                class="service-area-sectors__row"
              >
                <div class="service-area-sectors__col service-area-sectors__col--id">
                  {{ sector.id }}
                </div>
                <div class="service-area-sectors__col service-area-sectors__col--name">
                  <span class="service-area-sectors__name">{{ sector.name }}</span>
                </div>
                <div class="service-area-sectors__col service-area-sectors__col--equipment">
                  <div class="service-area-sectors__metrics">
                    <div
                      v-for="metric in sectorMetrics(sector.equipment_counts)"
                      :key="metric.key"
                      class="service-area-sectors__metric"
                      :class="{ 'service-area-sectors__metric--empty': metric.value === 0 }"
                      :title="metric.title"
                    >
                      <span class="service-area-sectors__metric-value">{{ metric.value }}</span>
                      <span class="service-area-sectors__metric-label">{{ metric.label }}</span>
                    </div>
                  </div>
                </div>
                <div class="service-area-sectors__col service-area-sectors__col--actions">
                  <v-tooltip v-if="$canRoute('MtoSectorDetail')" bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        text
                        small
                        v-bind="attrs"
                        v-on="on"
                        class="btn__icon pa-0"
                        width="18"
                        height="18"
                        :to="`/mto-sector/${sector.id}`"
                      >
                        <v-icon size="large">mdi-eye-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>Открыть участок</span>
                  </v-tooltip>
                </div>
              </div>
            </div>

            <div v-else class="service-area-sectors__empty-state">
              Участки не созданы
            </div>
          </div>
        </td>
      </template>
    </v-data-table>

    <service-area-form-dialog
      v-model="formDialog"
      :item="editItem"
      @saved="loadData"
    />

    <v-dialog v-model="deleteDialog" max-width="400" content-class="dialog-border-radius">
      <v-card>
        <v-card-title>Удалить район обслуживания?</v-card-title>
        <v-card-text v-if="deleteItem">
          «{{ deleteItem.name }}» будет удалён безвозвратно.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="deleteDialog = false">Отмена</v-btn>
          <v-btn color="error" text @click="doDelete">Удалить</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import BaseTextField from '@/components/BaseTextField';
import ServiceAreaFormDialog from '@/components/areas/ServiceAreaFormDialog';

export default {
  name: 'ServiceAreasList',
  components: { BaseTextField, ServiceAreaFormDialog },
  data () {
    return {
      loading: false,
      formDialog: false,
      editItem: null,
      deleteDialog: false,
      deleteItem: null,
      filterName: '',
      expanded: [],
      page: 1,
      itemsPerPage: 20,
      footerProps: {
        showFirstLastPage: true,
        'items-per-page-options': [10, 20, 50, 100],
        'items-per-page-text': 'Строк на странице',
      },
      headers: [
        { text: 'ID', value: 'id' },
        { text: 'Название', value: 'name' },
        { text: 'Мастер', value: 'master', sortable: false },
        { text: 'Участки', value: 'mto_sectors_count', sortable: false },
        { text: 'Показатели', value: 'equipment_counts', sortable: false },
        { text: '', value: 'actions', width: '10%', sortable: false, align: 'center' },
      ],
    };
  },
  created () {
    this.loadData();
  },
  watch: {
    filterName () {
      this.page = 1;
      this.expanded = [];
    },
  },
  computed: {
    ...mapGetters({
      serviceAreas: 'ServiceArea/serviceAreas',
    }),
    filteredItems () {
      return this.serviceAreas.filter((item) => {
        return !this.filterName || item.name.toLowerCase().includes(this.filterName.toLowerCase());
      });
    },
  },
  methods: {
    ...mapActions({
      getServiceAreas: 'ServiceArea/getServiceAreas',
      deleteServiceArea: 'ServiceArea/deleteServiceArea',
    }),
    async loadData () {
      this.loading = true;
      try {
        await this.getServiceAreas();
      } catch (e) {
        this.$flash_warning('Не удалось загрузить районы обслуживания');
      } finally {
        this.loading = false;
      }
    },
    hasEquipment (counts) {
      return this.equipmentChips(counts).length > 0;
    },
    equipmentChips (counts) {
      if (!counts) return [];

      const items = [
        { key: 'doorphones', label: 'Домофоны', value: counts.doorphones },
        { key: 'additional_entrances', label: 'Доп. входы', value: counts.additional_entrances },
        { key: 'gates_doorphones', label: 'Калитки (домофон)', value: counts.gates_doorphones },
        { key: 'gates_readers', label: 'Калитки (считыватель)', value: counts.gates_readers },
        { key: 'barriers', label: 'Шлагбаумы', value: counts.barriers },
        { key: 'apartments', label: 'Квартир', value: counts.apartments },
        { key: 'apartments_with_phone_setup', label: 'Возможна трубка', value: counts.apartments_with_phone_setup },
      ];

      return items.filter(item => Number(item.value) > 0);
    },
    sectorMetrics (counts) {
      const source = counts || {};

      return [
        { key: 'doorphones', label: 'Домофоны', title: 'Домофоны', value: Number(source.doorphones) || 0 },
        { key: 'additional_entrances', label: 'Доп. входы', title: 'Доп. входы', value: Number(source.additional_entrances) || 0 },
        { key: 'gates_doorphones', label: 'Кал. дом.', title: 'Калитки (домофон)', value: Number(source.gates_doorphones) || 0 },
        { key: 'gates_readers', label: 'Кал. счит.', title: 'Калитки (считыватель)', value: Number(source.gates_readers) || 0 },
        { key: 'barriers', label: 'Шлагбаумы', title: 'Шлагбаумы', value: Number(source.barriers) || 0 },
        { key: 'apartments', label: 'Квартиры', title: 'Всего квартир', value: Number(source.apartments) || 0 },
        { key: 'apartments_with_phone_setup', label: 'Возможна трубка', title: 'Квартир с возможностью установки трубки', value: Number(source.apartments_with_phone_setup) || 0 },
      ];
    },
    masterName (item) {
      return item?.master?.name || item?.masters?.[0]?.name || '';
    },
    isExpanded (item) {
      return this.expanded.some(row => row.id === item.id);
    },
    toggleExpand (item) {
      if (this.isExpanded(item)) {
        this.expanded = [];
      } else {
        this.expanded = [item];
      }
    },
    openCreate () {
      this.editItem = null;
      this.formDialog = true;
    },
    openEdit (item) {
      this.editItem = { ...item };
      this.formDialog = true;
    },
    confirmDelete (item) {
      this.deleteItem = item;
      this.deleteDialog = true;
    },
    async doDelete () {
      if (!this.deleteItem) return;
      try {
        await this.deleteServiceArea(this.deleteItem.id);
        this.$flash_success('Район обслуживания удалён');
        this.deleteDialog = false;
        this.deleteItem = null;
        await this.loadData();
      } catch (e) {
        this.$flash_warning('Не удалось удалить район обслуживания');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import './areasList.scss';

.areas-masters-text {
  color: #37474f;
  font-size: 0.875rem;
}

.areas-masters-text--interactive {
  cursor: default;
}

.service-area-expand-cell {
  padding: 0 !important;
  background: #f7f9fb;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.service-area-sectors {
  padding: 14px 16px 16px 48px;
}

.service-area-sectors__header {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.service-area-sectors__title {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #263238;
}

.service-area-sectors__table {
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 10px;
  overflow: hidden;
}

.service-area-sectors__row {
  display: grid;
  grid-template-columns: 64px minmax(140px, 0.9fr) minmax(360px, 2.4fr) 48px;
  gap: 12px;
  align-items: center;
  padding: 12px 14px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.service-area-sectors__row:first-child {
  border-top: none;
}

.service-area-sectors__row--head {
  background: #f5f8fa;
  border-top: none;
  padding-top: 8px;
  padding-bottom: 8px;
}

.service-area-sectors__row--head .service-area-sectors__col {
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #78909c;
}

.service-area-sectors__col {
  min-width: 0;
  font-size: 0.875rem;
  color: #37474f;
}

.service-area-sectors__col--actions {
  display: flex;
  justify-content: center;
}

.service-area-sectors__name {
  font-weight: 500;
  color: #263238;
  word-break: break-word;
}

.service-area-sectors__equipment {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.service-area-sectors__equipment-chip {
  height: 22px !important;
  font-size: 0.75rem !important;
}

.service-area-sectors__metrics {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(76px, 1fr));
  gap: 6px;
}

.service-area-sectors__metric {
  min-width: 0;
  padding: 6px 4px;
  border-radius: 8px;
  background: #f4f7fa;
  text-align: center;
}

.service-area-sectors__metric--empty {
  background: #f8fafb;
}

.service-area-sectors__metric-value {
  display: block;
  font-size: 0.9375rem;
  font-weight: 600;
  line-height: 1.2;
  color: #263238;
}

.service-area-sectors__metric--empty .service-area-sectors__metric-value {
  color: #90a4ae;
  font-weight: 500;
}

.service-area-sectors__metric-label {
  display: block;
  margin-top: 2px;
  font-size: 0.625rem;
  line-height: 1.2;
  color: #78909c;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.service-area-sectors__masters-card {
  min-width: 200px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.1);
}

.service-area-sectors__masters-title {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #78909c;
  margin-bottom: 8px;
}

.service-area-sectors__master-item {
  display: flex;
  align-items: center;
  color: #37474f;
  font-size: 0.875rem;
  line-height: 1.5;
}

.service-area-sectors__master-item + .service-area-sectors__master-item {
  margin-top: 4px;
}

.service-area-sectors__empty,
.service-area-sectors__empty-state {
  font-size: 0.875rem;
  color: #90a4ae;
}

.service-area-sectors__empty-state {
  padding: 8px 0 4px;
}
</style>
